// Epoch 55: Pricing Page - Platform Pricing Model
// Updated: November 27, 2025
// Removed Top Qualifier (in development), emphasizing Platform + Outreach Credits

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrialContext } from '../hooks/useTrialContext';
import { supabase } from '../supabase/client';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface PlanCardProps {
  title: string;
  price: string;
  priceSubtext?: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  isCurrentPlan?: boolean;
  onSelect: () => void;
  buttonText: string;
  disabled?: boolean;
  comingSoon?: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  title,
  price,
  priceSubtext,
  description,
  features,
  isPopular,
  isCurrentPlan,
  onSelect,
  buttonText,
  disabled,
  comingSoon
}) => {
  return (
    <div className={`
      relative bg-white rounded-xl shadow-lg border-2
      ${isCurrentPlan ? 'border-purple-500' : 'border-gray-200'}
      ${isPopular ? 'transform scale-105' : ''}
      p-6 flex flex-col h-full
    `}>
      {/* Popular badge */}
      {isPopular && !comingSoon && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Base Subscription
          </span>
        </div>
      )}

      {/* Coming Soon badge */}
      {comingSoon && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-yellow-500 text-gray-900 px-4 py-1 rounded-full text-sm font-semibold">
            Coming Soon
          </span>
        </div>
      )}

      {/* Current plan badge */}
      {isCurrentPlan && (
        <div className="absolute -top-4 right-4">
          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
            Current Plan
          </span>
        </div>
      )}

      {/* Plan header */}
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <div className="flex items-baseline mb-1">
          <span className="text-4xl font-bold text-gray-900">{price}</span>
          <span className="text-gray-500 ml-2">/month</span>
        </div>
        {priceSubtext && (
          <p className="text-sm text-gray-500 mb-2">{priceSubtext}</p>
        )}
        <p className="text-gray-600">{description}</p>
      </div>

      {/* Features list */}
      <ul className="space-y-3 mb-8 flex-grow">
        {features.map((feature, index) => {
          const isWarning = feature.startsWith('⚠️');
          const isNote = feature.startsWith('ℹ️');

          return (
            <li key={index} className="flex items-start">
              {!isWarning && !isNote && (
                <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              <span className={`${isWarning || isNote ? 'text-sm text-gray-600' : 'text-gray-700'}`}>
                {feature}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Action button */}
      <button
        onClick={onSelect}
        disabled={disabled}
        className={`
          w-full py-3 px-6 rounded-lg font-semibold transition-all
          ${disabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : isPopular
              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
              : 'bg-purple-600 text-white hover:bg-purple-700'
          }
        `}
      >
        {buttonText}
      </button>
    </div>
  );
};

const Pricing: React.FC = () => {
  useDocumentTitle('Pricing — Platform & Outreach Credits | Standing Encore');
  const navigate = useNavigate();
  const trial = useTrialContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<number | null>(null);

  // Determine current plan from trial context
  const currentPlan = trial.isPaidUser && trial.state.type === 'paid_user' ? trial.state.planType : null;

  // Dynamic header based on user state
  const getHeaderText = () => {
    if (trial.isTrialExpired) return "Your trial has expired - Subscribe to continue";
    if (trial.isTrialActive) return `${trial.daysRemaining} days left in trial - Choose your plan`;
    if (currentPlan) return "Manage Your Subscription";
    return "Simple, Transparent Pricing";
  };

  // Handle plan selection
  const handlePlatformSubscription = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // If not logged in at all, redirect to signup first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        window.location.href = '/signup?plan=platform';
        return;
      }

      // All logged-in users (including free_user) go straight to Stripe
      const { data, error: apiError } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          plan_type: 'platform',
          is_plan_change: !!currentPlan,
          current_plan: currentPlan
        }
      });

      if (apiError) {
        throw new Error(apiError.message || 'Failed to create checkout session');
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('❌ Platform subscription error:', err);
      setError(err instanceof Error ? err.message : 'Failed to subscribe to Platform');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle credit package purchase
  const handleCreditPurchase = async (packageSize: number) => {
    try {
      setPurchasing(packageSize);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        window.location.href = '/signup';
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/purchase-outreach-package`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            package_size: packageSize,
            success_url: `${window.location.origin}/dashboard?purchase=success`,
            cancel_url: `${window.location.origin}/pricing`,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Credit purchase error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start purchase');
      setPurchasing(null);
    }
  };

  // Determine button text for Platform
  const getPlatformButtonText = () => {
    if (isLoading) return 'Loading...';
    if (currentPlan === 'platform') return 'Current Plan';
    if (trial.isTrialActive) return 'Subscribe to Platform';
    if (trial.isTrialExpired) return 'Reactivate with Platform';
    return 'Subscribe — $30/month';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back nav */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {getHeaderText()}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Start with Platform for just $30/month. Add credits when you need them. No contracts, no commitments.
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg max-w-3xl mx-auto">
            {error}
          </div>
        )}

        {/* Platform Plan - Centered Hero */}
        <div className="max-w-lg mx-auto mb-16">
          <PlanCard
            title="Platform"
            price="$30"
            description="Full dashboard access for self-directed discovery campaigns"
            features={[
              'Full dashboard access',
              'Unlimited campaign management',
              'Access to existing leads in Leads Inbox',
              'Analytics & performance reporting',
              'Profile & company management',
              'Weekly intelligence reports',
              'CRM integrations (Coming Soon)',
              '⚠️ Outreach credits purchased separately'
            ]}
            isPopular={true}
            isCurrentPlan={currentPlan === 'platform'}
            onSelect={handlePlatformSubscription}
            buttonText={getPlatformButtonText()}
            disabled={isLoading || currentPlan === 'platform'}
          />
        </div>

        {/* Outreach Credits Section - Enhanced */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg p-8 max-w-5xl mx-auto mb-12 border-2 border-purple-200">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              💳 Pay As You Grow
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Outreach Credits</h2>
            <p className="text-lg text-gray-700 mb-2">
              Execute LinkedIn outreach campaigns at your own pace
            </p>
            <p className="text-gray-600">
              <strong>1 credit = 1 LinkedIn direct message thread</strong> (regardless of follow-up messages)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { size: 500, price: 200, perCredit: 0.40, popular: false, savings: null },
              { size: 1000, price: 380, perCredit: 0.38, popular: true, savings: 5 },
              { size: 2000, price: 744, perCredit: 0.37, popular: false, savings: 7 },
              { size: 5000, price: 1800, perCredit: 0.36, popular: false, savings: 10 }
            ].map((pkg) => (
              <div key={pkg.size} className={`
                relative border-2 rounded-lg p-5 flex flex-col
                ${pkg.popular ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white'}
              `}>
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-gray-900 mb-1">{pkg.size.toLocaleString()}</div>
                  <div className="text-sm text-gray-600 mb-2">credits</div>
                  <div className="text-2xl font-bold text-purple-600 mb-1">${pkg.price}</div>
                  <div className="text-xs text-gray-500">${pkg.perCredit.toFixed(2)}/credit</div>
                  {pkg.savings && (
                    <span className="inline-block mt-2 bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                      SAVE {pkg.savings}%
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleCreditPurchase(pkg.size)}
                  disabled={purchasing !== null}
                  className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-colors mt-auto ${
                    pkg.popular
                      ? 'bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:opacity-50'
                  } ${purchasing === pkg.size ? 'cursor-not-allowed' : ''}`}
                >
                  {purchasing === pkg.size ? 'Redirecting...' : 'Purchase Now'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works - Enhanced */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-8 max-w-4xl mx-auto mb-12">
          <h3 className="text-2xl font-bold text-blue-900 mb-6 text-center flex items-center justify-center">
            <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            How It Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-5 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">1</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Subscribe to Platform</h4>
                  <p className="text-sm text-gray-600">$30/month gives you full dashboard access, campaign management, existing leads, and CSR support</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-5 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">2</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Purchase Outreach Credits</h4>
                  <p className="text-sm text-gray-600">Buy credits when ready to launch campaigns. Start at $200 for 500 credits. Credits never expire!</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-5 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">3</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Create Your Campaigns</h4>
                  <p className="text-sm text-gray-600">Build LinkedIn outreach campaigns in the dashboard. Your CSR handles execution and tracks all responses</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-5 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">4</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Scale As You Grow</h4>
                  <p className="text-sm text-gray-600">Buy more credits anytime. No contracts or commitments. Perfect for agencies managing multiple clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What's Included Section */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">What's Included with Platform</h3>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Dashboard & Management', items: ['Full dashboard access', 'Unlimited campaign management', 'Company & profile settings'] },
                { title: 'Leads & Responses', items: ['Access to existing leads', 'Leads Inbox for responses', 'Lead response tracking'] },
                { title: 'Analytics & Reports', items: ['Campaign performance analytics', 'Lead response metrics', 'Export history tracking'] },
                { title: 'Support & Integrations', items: ['Dedicated CSR support', 'CRM integrations (Coming Soon)', 'Onboarding assistance'] }
              ].map((section, index) => (
                <div key={index} className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-3">{section.title}</h4>
                  <ul className="space-y-2">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-600">
                        <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                <strong className="text-gray-900">Note:</strong> Outreach credits are purchased separately based on your campaign volume. Credits never expire.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
